import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { QNear, Waypoint } from '../../../../../../util/machine-variable.utils';
import { Pose } from '../../../../../../util/pose.util';
import { useFlowServices } from '../../../hook/flow-service.hooks';
import { WaypointCaptureRef } from '../../../hook/waypoint-capture.hooks';
import { CaptureWaypointButton } from '../../button/capture-waypoint-button';
import { useFineTuneScriptLazy } from './flow-item-fine-tune.hooks';

export interface FineTuneButtonProps {
  onCapture: (waypoint: Waypoint, qnear: QNear) => void;
  onCaptureStart: () => void;
  onCaptureCancel: () => void;
  onLongPress?: () => void;
  executingIndex?: number;
  captureButtonText?: string;
  cancelButtonText?: string;
  closeOnCapture?: boolean;
}

export interface FineTuneButtonRef {
  moveToPosition: (pose: Pose) => Promise<void>;
}

const FineTuneButton = forwardRef((props: FineTuneButtonProps, ref) => {
  const {
    onCapture,
    onCaptureStart,
    onCaptureCancel,
    onLongPress,
    executingIndex,
    cancelButtonText,
    captureButtonText,
    closeOnCapture,
  } = props;

  const { flow } = useFlowServices();

  const waypointCaptureRef = useRef<WaypointCaptureRef>();
  const [isCapturing, setIsCapturing] = useState<boolean>(false);

  const {
    close: stopFineTune,
    executeFineTune: startFineTune,
    moveToPosition,
  } = useFineTuneScriptLazy({
    toolConfig: flow.config.torch,
    executingIndex: executingIndex ?? 0,
  });

  useImperativeHandle(ref, () => {
    return {
      moveToPosition,
    };
  });

  const onCaptureInternal = useCallback(
    (waypoint: Waypoint, qnear: QNear) => {
      if (closeOnCapture) {
        setIsCapturing(false);
      }

      if (typeof onCapture === 'function') {
        onCapture(waypoint, qnear);
      }
    },
    [onCapture, closeOnCapture]
  );

  const onCaptureStartInternal = useCallback(() => {
    setIsCapturing(true);

    if (typeof onCaptureStart === 'function') {
      onCaptureStart();
    }
  }, [onCaptureStart]);

  const onCaptureCancelInternal = useCallback(() => {
    setIsCapturing(false);

    if (typeof onCaptureCancel === 'function') {
      onCaptureCancel();
    }
  }, [onCaptureCancel]);

  useEffect(() => {
    if (isCapturing) {
      startFineTune();
    } else {
      stopFineTune();
    }
  }, [isCapturing, startFineTune, stopFineTune]);

  return (
    <CaptureWaypointButton
      innerRef={waypointCaptureRef}
      onLongPress={onLongPress}
      onCapture={onCaptureInternal}
      onCaptureStart={onCaptureStartInternal}
      onCaptureCancel={onCaptureCancelInternal}
      cancelButtonText={cancelButtonText}
      captureButtonText={captureButtonText}
      stopProgramFirst={true}
    />
  );
});

FineTuneButton.displayName = 'FineTuneButton';

export { FineTuneButton };
